import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { NavBar } from "@/components/NavBar";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import {
  FileSearch,
  ScanSearch,
  BadgeCheck,
  ArrowRight,
  Scale,
  Banknote,
  PlugZap,
  ShieldCheck,
  Baby,
  Car,
  Gavel,
} from "lucide-react";

const steps = [
  {
    icon: FileSearch,
    title: "1. Submit your matter",
    body: "Enter your court, loan, utility, birth certificate, traffic ticket, or warrant details and upload the supporting document.",
  },
  {
    icon: ScanSearch,
    title: "2. Automated verification",
    body: "Docling converts your document and our decision engine compares every required field against what you entered — differences come back to you for review.",
  },
  {
    icon: BadgeCheck,
    title: "3. Order verified reports",
    body: "Purchase verified report requests at $250 each as a member of the association. Our operations team processes every paid request — allow 30–60 days.",
  },
];

const matters = [
  { icon: Scale, label: "Court matters", body: "Case number, court district, and filed date verified against the complaint first page." },
  { icon: Banknote, label: "Loan matters", body: "Loan or account number verified against the promissory note &/or monthly loan statement." },
  { icon: PlugZap, label: "Utility matters", body: "Account number verified against a monthly utility statement." },
  { icon: Baby, label: "Birth certificates", body: "Certificate number verified against a copy of the birth certificate." },
  { icon: Car, label: "Traffic tickets", body: "Ticket or citation number verified against a copy of the ticket." },
  { icon: Gavel, label: "Warrants", body: "The warrant number listed on the warrant verified against a copy of the warrant." },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <NavBar />
      <main className="flex-1">
        {/* Hero */}
        <section className="hero-wash noise-overlay border-b border-border">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-6"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary" data-testid="pma-badge">
                <ShieldCheck className="h-3.5 w-3.5" /> A Private Membership Association
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
                Document information, verified before you pay.
              </h1>
              <p className="text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
                GetYourQSIP operates as a private membership association. Members submit court, loan, utility,
                birth certificate, traffic ticket, and warrant matters with supporting documents. Every entry is
                machine-verified against the document itself — then verified requests become report orders
                processed by our operations team.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  size="lg"
                  data-testid="public-hero-primary-cta"
                  onClick={() => navigate(user ? "/dashboard" : "/membership")}
                >
                  {user ? "Open Dashboard" : "View Membership"} <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="public-hero-secondary-cta"
                  onClick={() => navigate("/info")}
                >
                  Read the Disclosure
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative hidden lg:block"
            >
              <div className="overflow-hidden rounded-2xl border border-border shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1611416370495-50fac9e1b382?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxleGVjdXRpdmUlMjBvZmZpY2UlMjBuaWdodCUyMGNpdHklMjBza3lsaW5lJTIwZGFya3xlbnwwfHx8Ymx1ZXwxNzg2MjIwMTIwfDA&ixlib=rb-4.1.0&q=85&w=1200"
                  alt="City skyline at night representing institutional trust"
                  className="h-[420px] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 rounded-xl border border-border bg-card/95 p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Member report</span>
                    <span className="font-mono text-primary">$250 / report</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Verification before payment</span>
                    <span className="font-mono text-[hsl(var(--status-verified))]">Always</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* How it works */}
        <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">How the workflow runs</h2>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            A controlled, auditable path from submission to fulfillment — verification always comes before payment.
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: index * 0.08 }}
              >
                <Card className="h-full bg-card border-border transition-shadow hover:shadow-lg hover:shadow-primary/5">
                  <CardContent className="p-6 space-y-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/15 border border-primary/30">
                      <step.icon className="h-5 w-5 text-primary" />
                    </span>
                    <h3 className="font-display text-lg font-semibold">{step.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Matter types */}
        <section className="border-t border-border bg-card/40">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Six matter types, one standard</h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 md:grid-cols-3">
              {matters.map((matter) => (
                <div key={matter.label} className="rounded-xl border border-border bg-card p-6 space-y-2.5">
                  <matter.icon className="h-5 w-5 text-primary" />
                  <h3 className="font-medium">{matter.label}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{matter.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button data-testid="home-membership-cta" onClick={() => navigate("/membership")}>
                Compare membership tiers <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
              <Link to="/info" data-testid="home-info-link" className="text-sm text-primary hover:underline">
                Learn how verification works
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
